export type CatalogViewMode = "magazine" | "power-buyer";

export const VIEW_MODE_KEY = "mag-portal-catalog-view";
// PASTE #393 C: mirror the saved view into a COOKIE so the server can read it and
// SSR the correct view on first paint (localStorage is invisible to the server,
// which is why the page opened in Magazine then flashed to Power Buyer).
export const VIEW_MODE_COOKIE = "mag-catalog-view";

// Pure validator - usable on the server (no window/localStorage).
export function parseViewMode(
  v: string | null | undefined,
): CatalogViewMode | null {
  return v === "magazine" || v === "power-buyer" ? v : null;
}

// The view the catalog should render: an explicit ?view= wins (deep links keep
// working), else the saved cookie, else the Magazine default. Pure + testable.
export function resolveCatalogView(
  viewParam: string | null | undefined,
  cookieView: CatalogViewMode | null,
): CatalogViewMode {
  return parseViewMode(viewParam) ?? cookieView ?? "magazine";
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

// Cookie first (what the server also reads), then legacy localStorage.
export function readViewMode(): CatalogViewMode | null {
  const cookie = parseViewMode(readCookie(VIEW_MODE_COOKIE));
  if (cookie) return cookie;
  try {
    return parseViewMode(window.localStorage.getItem(VIEW_MODE_KEY));
  } catch {
    return null;
  }
}

export function writeViewMode(mode: CatalogViewMode): void {
  try {
    window.localStorage.setItem(VIEW_MODE_KEY, mode);
  } catch {
    // ignore
  }
  try {
    // 1 year; Lax so it rides top-level catalog navigations. Not HttpOnly - the
    // client toggle must read/write it, and it carries no sensitive data.
    document.cookie = `${VIEW_MODE_COOKIE}=${mode}; path=/; max-age=31536000; SameSite=Lax`;
  } catch {
    // ignore
  }
}

export function clearViewMode(): void {
  try {
    window.localStorage.removeItem(VIEW_MODE_KEY);
  } catch {
    // ignore
  }
  try {
    document.cookie = `${VIEW_MODE_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
  } catch {
    // ignore
  }
}

// PASTE #374: deep-link into the SIGNED-IN catalog filtered to a category,
// carrying the buyer's active view mode so power-buyer vs magazine (+ its
// pricing context) survives the round trip. Never the marketing hub.
export function catalogBrowseHref(
  category: string,
  viewMode: CatalogViewMode | null,
): string {
  const params = new URLSearchParams({ category });
  if (viewMode) params.set("view", viewMode);
  return `/catalog?${params.toString()}`;
}
